/* EBB — Predictive search
   Self-contained, additive script (same doctrine as ebb-hospitality.js).
   The nav search icon calls window.ebbOpenSearch(); if this file fails to
   load, the icon falls back to navigating to /search. Panel DOM is built
   lazily on first open. Uses Shopify's native predictive search endpoint. */
(function () {
  'use strict';

  var DEBOUNCE_MS = 250;
  var MIN_CHARS = 2;

  var built = false;
  var isOpen = false;
  var debounceTimer = null;
  var activeController = null;
  var lastFocused = null;

  var ov, panel, input, body;

  /* ── DOM builders (no innerHTML for user-derived strings) ── */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text) n.textContent = text;
    return n;
  }

  function buildPanel() {
    if (built) return;
    built = true;

    ov = el('div', 'es-ov');
    ov.id = 'esOv';
    ov.addEventListener('click', closeSearch);

    panel = el('div', 'es-panel');
    panel.id = 'esPanel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', 'Search');

    var inner = el('div', 'es-inner');
    var hd = el('div', 'es-hd');

    var form = el('form', 'es-input-wrap');
    form.setAttribute('action', '/search');
    form.setAttribute('method', 'get');
    form.setAttribute('role', 'search');

    var svgNS = 'http://www.w3.org/2000/svg';
    var icon = document.createElementNS(svgNS, 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('class', 'es-input-icon');
    var c = document.createElementNS(svgNS, 'circle');
    c.setAttribute('cx', '11'); c.setAttribute('cy', '11'); c.setAttribute('r', '7');
    var l = document.createElementNS(svgNS, 'line');
    l.setAttribute('x1', '16.5'); l.setAttribute('y1', '16.5'); l.setAttribute('x2', '22'); l.setAttribute('y2', '22');
    icon.appendChild(c); icon.appendChild(l);

    input = el('input', 'es-input');
    input.type = 'search';
    input.name = 'q';
    input.id = 'esInput';
    input.placeholder = 'Search products';
    input.autocomplete = 'off';
    input.setAttribute('aria-label', 'Search');

    form.appendChild(icon);
    form.appendChild(input);
    form.addEventListener('submit', function (e) {
      var q = input.value.trim();
      if (!q) { e.preventDefault(); return; }
      // allow native GET to /search?q=
    });

    var x = el('button', 'es-x');
    x.type = 'button';
    x.setAttribute('aria-label', 'Close search');
    var xsvg = document.createElementNS(svgNS, 'svg');
    xsvg.setAttribute('viewBox', '0 0 24 24');
    var x1 = document.createElementNS(svgNS, 'line');
    x1.setAttribute('x1', '18'); x1.setAttribute('y1', '6'); x1.setAttribute('x2', '6'); x1.setAttribute('y2', '18');
    var x2 = document.createElementNS(svgNS, 'line');
    x2.setAttribute('x1', '6'); x2.setAttribute('y1', '6'); x2.setAttribute('x2', '18'); x2.setAttribute('y2', '18');
    xsvg.appendChild(x1); xsvg.appendChild(x2);
    x.appendChild(xsvg);
    x.addEventListener('click', closeSearch);

    hd.appendChild(form);
    hd.appendChild(x);
    inner.appendChild(hd);
    panel.appendChild(inner);

    body = el('div', 'es-body');
    var results = el('div', 'es-results');
    results.id = 'esResults';
    results.setAttribute('aria-live', 'polite');
    body.appendChild(results);
    panel.appendChild(body);

    document.body.appendChild(ov);
    document.body.appendChild(panel);

    input.addEventListener('input', onType);
    panel.addEventListener('keydown', onKeydown);
  }

  /* ── Open / close ── */

  function openSearch() {
    try {
      buildPanel();
    } catch (e) {
      window.location = '/search';
      return;
    }
    if (isOpen) { input.focus(); return; }
    isOpen = true;
    lastFocused = document.activeElement;
    ov.classList.add('on');
    panel.classList.add('on');
    document.addEventListener('keydown', onDocKeydown);
    window.setTimeout(function () { input.focus(); }, 60);
  }

  function closeSearch() {
    if (!isOpen) return;
    isOpen = false;
    ov.classList.remove('on');
    panel.classList.remove('on');
    document.removeEventListener('keydown', onDocKeydown);
    if (activeController) { try { activeController.abort(); } catch (e) {} activeController = null; }
    if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
    if (lastFocused && typeof lastFocused.focus === 'function') {
      try { lastFocused.focus(); } catch (e) {}
    }
  }

  function onDocKeydown(e) {
    if (e.key === 'Escape') { e.preventDefault(); closeSearch(); }
  }

  /* ── Keyboard: arrows move through results, Enter submits from input ── */

  function onKeydown(e) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    var items = panel.querySelectorAll('.es-prod, .es-link, .es-all');
    if (!items.length) return;
    e.preventDefault();
    var list = Array.prototype.slice.call(items);
    var idx = list.indexOf(document.activeElement);
    var next;
    if (e.key === 'ArrowDown') {
      next = idx === -1 ? list[0] : list[Math.min(idx + 1, list.length - 1)];
    } else {
      next = idx <= 0 ? input : list[idx - 1];
    }
    if (next && typeof next.focus === 'function') next.focus();
  }

  /* ── Typing → debounced fetch ── */

  function onType() {
    if (debounceTimer) clearTimeout(debounceTimer);
    var q = input.value.trim();
    if (q.length < MIN_CHARS) {
      renderStatus('');
      return;
    }
    debounceTimer = window.setTimeout(function () { runQuery(q); }, DEBOUNCE_MS);
  }

  function runQuery(q) {
    if (activeController) { try { activeController.abort(); } catch (e) {} }
    var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    activeController = controller;

    renderStatus('Searching…');

    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product,page,collection' +
      '&resources[limit]=6' +
      '&resources[options][unavailable_products]=hide';

    fetch(url, controller ? { signal: controller.signal } : undefined)
      .then(function (r) {
        if (!r.ok) throw new Error('bad status');
        return r.json();
      })
      .then(function (d) {
        if (controller && controller !== activeController) return; // stale
        if (input.value.trim() !== q) return; // stale
        renderResults(d, q);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        renderStatus('Search is having a moment — press enter to search the full site.');
      });
  }

  /* ── Rendering ── */

  function resultsEl() {
    return document.getElementById('esResults');
  }

  function renderStatus(msg) {
    var r = resultsEl();
    if (!r) return;
    r.textContent = '';
    if (msg) r.appendChild(el('div', 'es-status', msg));
  }

  function thumbUrl(u) {
    if (!u) return null;
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=120';
  }

  function renderResults(d, q) {
    var r = resultsEl();
    if (!r) return;
    r.textContent = '';

    var res = (d && d.resources && d.resources.results) || {};
    // PageBot: hide hidden-tagged products and the $0 free launch hat from the live results (as the grids do)
    var products = (res.products || []).filter(function (p) {
      var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
      return p.handle !== 'launch-hat-free-gift' && tags.indexOf('hidden') === -1;
    });
    var collections = res.collections || [];
    var pages = res.pages || [];

    if (!products.length && !collections.length && !pages.length) {
      r.appendChild(el('div', 'es-status', 'Nothing yet — try a different word'));
      return;
    }

    if (products.length) {
      r.appendChild(el('div', 'es-group-hd', 'Products'));
      products.forEach(function (p) {
        var a = el('a', 'es-prod');
        a.href = p.url;
        var imgSrc = (p.featured_image && p.featured_image.url) || p.image || null;
        if (imgSrc) {
          var img = el('img', 'es-prod-img');
          img.src = thumbUrl(imgSrc);
          img.alt = (p.featured_image && p.featured_image.alt) || p.title || '';
          img.loading = 'lazy';
          img.width = 52; img.height = 52;
          a.appendChild(img);
        } else {
          a.appendChild(el('div', 'es-prod-noimg'));
        }
        var info = el('div', 'es-prod-info');
        info.appendChild(el('div', 'es-prod-name', p.title || ''));
        if (p.price) info.appendChild(el('div', 'es-prod-price', '$' + p.price));
        a.appendChild(info);
        r.appendChild(a);
      });
    }

    if (collections.length) {
      r.appendChild(el('div', 'es-group-hd', 'Collections'));
      collections.forEach(function (col) {
        var a = el('a', 'es-link', col.title || '');
        a.href = col.url;
        r.appendChild(a);
      });
    }

    if (pages.length) {
      r.appendChild(el('div', 'es-group-hd', 'Pages'));
      pages.forEach(function (pg) {
        var a = el('a', 'es-link', pg.title || '');
        a.href = pg.url;
        r.appendChild(a);
      });
    }

    var all = el('a', 'es-all', 'See all results');
    all.href = '/search?q=' + encodeURIComponent(q);
    r.appendChild(all);
  }

  /* ── Public hook for the nav icon ── */
  window.ebbOpenSearch = openSearch;
})();
